import React, { memo, useCallback } from 'react';

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TRADING_TYPE_OPTIONS = [
  { value: 'INTRADAY', label: 'Intraday' },
  { value: 'POSITIONAL', label: 'Positional' },
];

const ORDER_TYPE_OPTIONS = [
  { value: 'MARKET_ORDER', label: 'Market Order' },
  { value: 'LIMIT_ORDER', label: 'Limit Order' },
];

const formatINR = (value) => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('en-IN').format(value);
};

const parseINR = (formattedValue) => {
  if (!formattedValue) return null;
  const numericValue = formattedValue.replace(/,/g, '');
  const parsed = parseFloat(numericValue);
  return isNaN(parsed) ? null : parsed;
};

const RiskManagementStep = memo(({ data, errors = {}, onChange, compact = false }) => {
  const handleFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    onChange(name, value);
  }, [onChange]);

  const handleCapitalChange = useCallback((event) => {
    const rawValue = event.target.value;
    const numericValue = parseINR(rawValue);
    onChange('capitalAmount', numericValue);
  }, [onChange]);

  const handleLotSizeChange = useCallback((event) => {
    const raw = event.target.value;
    if (raw === '' || raw == null) {
      onChange('lotSize', null);
    } else {
      const parsed = parseInt(raw, 10);
      onChange('lotSize', isNaN(parsed) ? null : parsed);
    }
  }, [onChange]);

  if (compact) {
    return (
      <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
        {/* Capital Amount */}
        <div className="lg:col-span-3 form-control w-full">
          <input
            type="text"
            name="capitalAmount"
            placeholder="Capital Amount (Optional)"
            className={`input input-bordered bg-base-200 input-sm w-full ${errors.capitalAmount ? 'input-error' : ''}`}
            value={data.capitalAmount ? formatINR(data.capitalAmount) : ''}
            onChange={handleCapitalChange}
          />
          {errors.capitalAmount && <span className="text-xs text-error mt-1">{errors.capitalAmount}</span>}
        </div>

        {/* Lot Size — compact */}
        <div className="lg:col-span-3 form-control w-full">
          <input
            type="number"
            name="lotSize"
            placeholder="Lot Size (Default: 1)"
            min="1"
            step="1"
            className={`input input-bordered bg-base-200 input-sm w-full ${errors.lotSize ? 'input-error' : ''}`}
            value={data.lotSize ?? ''}
            onChange={handleLotSizeChange}
          />
          {errors.lotSize && <span className="text-xs text-error mt-1">{errors.lotSize}</span>}
        </div>

        {/* Trading Type */}
        <div className="lg:col-span-3 form-control w-full">
          <select
            name="tradingType"
            className={`select select-bordered bg-base-200 select-sm w-full ${errors.tradingType ? 'select-error' : ''}`}
            value={data.tradingType || 'INTRADAY'}
            onChange={handleFieldChange}
          >
            {TRADING_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.tradingType && <span className="text-xs text-error mt-1">{errors.tradingType}</span>}
        </div>

        {/* Order Type */}
        <div className="lg:col-span-3 form-control w-full">
          <select
            name="orderType"
            className={`select select-bordered bg-base-200 select-sm w-full ${errors.orderType ? 'select-error' : ''}`}
            value={data.orderType || 'MARKET_ORDER'}
            onChange={handleFieldChange}
          >
            {ORDER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.orderType && <span className="text-xs text-error mt-1">{errors.orderType}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-2">Risk Management</h2>
      <p className="text-sm text-base-content/60 mb-6">
        Configure capital allocation and order execution settings
      </p>

      <div className="space-y-4">
        {/* Capital Amount */}
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm">Capital Amount</span>
            <span className="text-xs text-base-content/60 tooltip" data-tip="Initial capital for this strategy (₹1,000 - ₹1,00,00,000)">
              <InfoIcon />
            </span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/60">₹</span>
            <input
              type="text"
              name="capitalAmount"
              placeholder="e.g., 50,000"
              className={`input input-bordered bg-base-200 w-full pl-8 ${errors.capitalAmount ? 'input-error' : ''}`}
              value={data.capitalAmount ? formatINR(data.capitalAmount) : ''}
              onChange={handleCapitalChange}
            />
          </div>
          <label className="label">
            <span className={`text-xs text-base-content/60 ${errors.capitalAmount ? 'text-error' : ''}`}>
              {errors.capitalAmount || 'Optional. Minimum ₹1,000, Maximum ₹1,00,00,000'}
            </span>
          </label>
        </div>

        {/* Lot Size — full mode */}
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm">Lot Size</span>
            <span className="text-xs text-base-content/60 tooltip" data-tip="Number of units to buy/sell per signal. Leave blank to use the default (1).">
              <InfoIcon />
            </span>
          </label>
          <input
            type="number"
            name="lotSize"
            placeholder="e.g., 5"
            min="1"
            step="1"
            className={`input input-bordered bg-base-200 w-full ${errors.lotSize ? 'input-error' : ''}`}
            value={data.lotSize ?? ''}
            onChange={handleLotSizeChange}
          />
          <label className="label">
            <span className={`text-xs text-base-content/60 ${errors.lotSize ? 'text-error' : ''}`}>
              {errors.lotSize || 'Optional. Units per BUY/SELL signal. Default: 1'}
            </span>
          </label>
        </div>

        {/* Trading Type & Order Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">Trading Type <span className="text-error">*</span></span>
              <span className="text-xs text-base-content/60 tooltip" data-tip="Intraday: positions closed same day. Positional: positions held overnight">
                <InfoIcon />
              </span>
            </label>
            <select
              name="tradingType"
              className={`select select-bordered bg-base-200 w-full ${errors.tradingType ? 'select-error' : ''}`}
              value={data.tradingType || 'INTRADAY'}
              onChange={handleFieldChange}
            >
              {TRADING_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label className="label">
              <span className={`text-xs text-base-content/60 ${errors.tradingType ? 'text-error' : ''}`}>
                {errors.tradingType || 'Select trading duration type'}
              </span>
            </label>
          </div>

          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">Order Type <span className="text-error">*</span></span>
              <span className="text-xs text-base-content/60 tooltip" data-tip="Market Order: immediate execution. Limit Order: execution at specified price">
                <InfoIcon />
              </span>
            </label>
            <select
              name="orderType"
              className={`select select-bordered bg-base-200 w-full ${errors.orderType ? 'select-error' : ''}`}
              value={data.orderType || 'MARKET_ORDER'}
              onChange={handleFieldChange}
            >
              {ORDER_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label className="label">
              <span className={`text-xs text-base-content/60 ${errors.orderType ? 'text-error' : ''}`}>
                {errors.orderType || 'Select order execution type'}
              </span>
            </label>
          </div>
        </div>

        {/* Stop Loss / Target Profit / Trailing Stop Loss Section */}
        <div className="divider my-6">Exit Risk Controls</div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Stop Loss */}
          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">Stop Loss</span>
              <span className="text-xs text-base-content/60 tooltip" data-tip="Exit if position loses more than this percentage">
                <InfoIcon />
              </span>
            </label>
            <div className="relative">
              <input
                type="number"
                name="stopLossPercent"
                placeholder="e.g., 2.0"
                step="0.1"
                min="0"
                max="100"
                className={`input input-bordered bg-base-200 w-full pr-8 ${errors.stopLossPercent ? 'input-error' : ''}`}
                value={data.riskManagement?.stopLossPercent ?? ''}
                onChange={(e) => onChange('riskManagement', {
                  ...data.riskManagement,
                  stopLossPercent: e.target.value ? parseFloat(e.target.value) : null
                })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/60">%</span>
            </div>
            <label className="label">
              <span className={`text-xs text-base-content/60 ${errors.stopLossPercent ? 'text-error' : ''}`}>
                {errors.stopLossPercent || 'Optional. Max loss per trade'}
              </span>
            </label>
          </div>

          {/* Target Profit */}
          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">Target Profit</span>
              <span className="text-xs text-base-content/60 tooltip" data-tip="Exit when position gains more than this percentage">
                <InfoIcon />
              </span>
            </label>
            <div className="relative">
              <input
                type="number"
                name="targetProfitPercent"
                placeholder="e.g., 5.0"
                step="0.1"
                min="0"
                max="100"
                className={`input input-bordered bg-base-200 w-full pr-8 ${errors.targetProfitPercent ? 'input-error' : ''}`}
                value={data.riskManagement?.targetProfitPercent ?? ''}
                onChange={(e) => onChange('riskManagement', {
                  ...data.riskManagement,
                  targetProfitPercent: e.target.value ? parseFloat(e.target.value) : null
                })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/60">%</span>
            </div>
            <label className="label">
              <span className={`text-xs text-base-content/60 ${errors.targetProfitPercent ? 'text-error' : ''}`}>
                {errors.targetProfitPercent || 'Optional. Take profit level'}
              </span>
            </label>
          </div>

          {/* Trailing Stop Loss */}
          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">Trailing Stop</span>
              <span className="text-xs text-base-content/60 tooltip" data-tip="Trail stop loss by this % below highest price since entry">
                <InfoIcon />
              </span>
            </label>
            <div className="relative">
              <input
                type="number"
                name="trailingStopPercent"
                placeholder="e.g., 1.5"
                step="0.1"
                min="0"
                max="100"
                className={`input input-bordered bg-base-200 w-full pr-8 ${errors.trailingStopPercent ? 'input-error' : ''}`}
                value={data.riskManagement?.trailingStopPercent ?? ''}
                onChange={(e) => onChange('riskManagement', {
                  ...data.riskManagement,
                  trailingStopPercent: e.target.value ? parseFloat(e.target.value) : null
                })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/60">%</span>
            </div>
            <label className="label">
              <span className={`text-xs text-base-content/60 ${errors.trailingStopPercent ? 'text-error' : ''}`}>
                {errors.trailingStopPercent || 'Optional. Trailing stop distance'}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
});

export default RiskManagementStep;
